import { useEffect, useState } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useApi, errMsg, FAMILY_LABEL, MODE_LABEL } from "@/lib/useApi";
import { Zap, Clock, FileCheck2, AlertTriangle } from "lucide-react";

const MODE_META = {
  quick: { icon: Zap, time: "15 minutes", desc: "Scaled from the official pattern" },
  intermediate: { icon: Clock, time: "90 minutes", desc: "Scaled from the official pattern" },
  full: { icon: FileCheck2, time: "Official duration", desc: "Exact verified blueprint" },
};

export default function PracticePage({ modes = ["quick", "intermediate"], title = "Practice", blurb = "" }) {
  const { me } = useOutletContext();
  const api = useApi();
  const nav = useNavigate();
  const fams = me?.selected_families || [];
  const [family, setFamily] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [picked, setPicked] = useState([]);
  const [mode, setMode] = useState(modes[0]);
  const [allowSeen, setAllowSeen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [resumeId, setResumeId] = useState(null);

  useEffect(() => { if (fams.length && !family) setFamily(fams[0]); }, [fams, family]);
  useEffect(() => { api.get("/exams/eligibility").then((r) => setResumeId(r.data.in_progress_attempt_id)).catch(() => {}); }, [api]);
  useEffect(() => {
    if (!family) return;
    const mine = new Set((me?.selected_subjects || []).filter((s) => s.startsWith(family)).map((s) => s.split(":")[1]));
    api.get(`/subjects?family=${family}`).then((r) => setSubjects(r.data.filter((s) => mine.has(s.code))));
    setPicked([]);
  }, [family, me, api]);

  const single = family === "CSIR_NET" || mode === "full";
  useEffect(() => { if (single && picked.length > 1) setPicked(picked.slice(0, 1)); }, [single, picked]);

  useEffect(() => {
    setPreview(null); setErr("");
    if (!family || !picked.length) return;
    api.post("/exams/preview", { family, subjects: picked, mode, allow_seen: allowSeen })
      .then((r) => { setPreview(r.data); setResumeId(r.data.eligibility.in_progress_attempt_id); })
      .catch((e) => setErr(errMsg(e)));
  }, [family, picked, mode, allowSeen, api]);

  const toggle = (code) => setPicked((cur) => (single ? [code] : cur.includes(code) ? cur.filter((c) => c !== code) : [...cur, code]));

  const start = async () => {
    setBusy(true);
    try {
      const r = await api.post("/exams/start", { family, subjects: picked, mode, allow_seen: allowSeen });
      nav(`/exam/${r.data.attempt_id}`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.attempt_id) setResumeId(d.attempt_id);
      toast.error(errMsg(e));
      setErr(errMsg(e));
    } finally { setBusy(false); }
  };

  const elig = preview?.eligibility;
  const blocked = !preview || preview.shortages.length > 0 || !elig?.can_start;

  return (
    <div className="space-y-6 max-w-5xl" data-testid="exam-setup-page">
      <div>
        <h1 className="font-display font-bold text-2xl sm:text-3xl text-slate-900 tracking-tight">{title}</h1>
        <p className="text-slate-600 mt-2 text-sm">{blurb}</p>
      </div>

      {resumeId && (
        <Alert className="border-blue-200 bg-blue-50" data-testid="resume-alert">
          <AlertTitle>You have an exam in progress</AlertTitle>
          <AlertDescription className="flex items-center justify-between gap-3">
            <span>Finish or submit it before starting another.</span>
            <Button size="sm" onClick={() => nav(`/exam/${resumeId}`)} data-testid="resume-exam-btn">Resume</Button>
          </AlertDescription>
        </Alert>
      )}

      <Card className="p-6 border-slate-200 space-y-6">
        <Step n="1" label="Exam family">
          <div className="flex gap-2 flex-wrap">
            {fams.map((f) => (
              <Chip key={f} active={family === f} onClick={() => setFamily(f)} testid={`setup-family-${f}`}>{FAMILY_LABEL[f]}</Chip>
            ))}
            {!fams.length && <Link to="/app/my-subjects" className="text-sm text-blue-600">Choose your exams first</Link>}
          </div>
        </Step>
        <Step n="2" label={single ? "Subject (one per attempt)" : "Subjects (one or more)"}>
          <div className="flex gap-2 flex-wrap">
            {subjects.map((s) => (
              <Chip key={s.code} active={picked.includes(s.code)} onClick={() => toggle(s.code)} testid={`setup-subject-${s.code}`}>
                <span className="font-mono text-xs mr-1.5 opacity-70">{s.code}</span>{s.name.length > 48 ? s.name.slice(0, 48) + "…" : s.name}
              </Chip>
            ))}
            {family && !subjects.length && <Link to="/app/my-subjects" className="text-sm text-blue-600">Add subjects for this exam</Link>}
          </div>
        </Step>
        <Step n="3" label="Mode">
          <div className="grid sm:grid-cols-3 gap-3">
            {modes.map((md) => {
              const M = MODE_META[md];
              return (
                <button key={md} onClick={() => setMode(md)} data-testid={`practice-mode-${md}-btn`}
                  className={`text-left p-4 rounded-xl border transition-colors ${mode === md ? "border-blue-500 bg-blue-50/50 ring-2 ring-blue-500/20" : "border-slate-200 hover:border-blue-300"}`}>
                  <M.icon className="h-5 w-5 text-blue-600" />
                  <p className="font-display font-semibold mt-3">{MODE_LABEL[md]}</p>
                  <p className="text-xs text-slate-500 mt-1">{M.time} · {M.desc}</p>
                </button>
              );
            })}
          </div>
        </Step>
      </Card>

      {err && (
        <Alert variant="destructive" className="bg-white" data-testid="setup-error">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            {err} {/subscribe/i.test(err) && <Link to="/app/subscription" className="underline ml-1">View plans</Link>}
          </AlertDescription>
        </Alert>
      )}

      {preview && (
        <Card className="p-6 border-slate-200 space-y-4" data-testid="exam-preview">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-700" data-testid="exam-label">{preview.label}</Badge>
            <Badge variant="outline" className={preview.blueprint.verification_status === "verified" ? "border-emerald-200 text-emerald-700" : "border-amber-200 text-amber-700"}>
              Blueprint {preview.blueprint.version} · {preview.blueprint.verification_status}
            </Badge>
          </div>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Section</TableHead><TableHead>Questions</TableHead><TableHead>Answer up to</TableHead><TableHead>Marks</TableHead><TableHead>Unseen available</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {preview.sections.map((s) => (
                <TableRow key={s.name}>
                  <TableCell className="font-medium">{s.name}{s.pool !== "SUBJECT" && <span className="text-xs text-slate-500 ml-2">shared pool</span>}</TableCell>
                  <TableCell>{s.count}</TableCell><TableCell>{s.attempts_allowed}</TableCell>
                  <TableCell className="font-mono text-xs">+{s.marks_correct} / {s.marks_incorrect}</TableCell>
                  <TableCell className={s.unseen < s.count ? "text-red-600" : ""}>{s.unseen} of {s.published}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {preview.shortages.length > 0 && (
            <Alert className="border-amber-200 bg-amber-50" data-testid="shortage-alert">
              <AlertTitle>Not enough unseen questions yet</AlertTitle>
              <AlertDescription className="space-y-2">
                {preview.shortages.map((s) => (
                  <p key={s.section} className="text-sm">Section {s.section}: needs {s.needed}, {s.unseen} unseen of {s.published} published.</p>
                ))}
                {preview.shortages.every((s) => s.repeat_possible) ? (
                  <label className="flex items-center gap-2 text-sm"><Checkbox checked={allowSeen} onCheckedChange={(v) => setAllowSeen(!!v)} data-testid="allow-seen-checkbox" />
                    Include questions I've already seen to fill the gap</label>
                ) : <p className="text-sm">The question bank for this selection is still being built. Please try another mode or check back later.</p>}
              </AlertDescription>
            </Alert>
          )}
          {allowSeen && <p className="text-xs text-amber-700">Repeats enabled for this attempt only.</p>}
          <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
            <p className="text-sm text-slate-600" data-testid="setup-eligibility">
              {elig.subscription.active ? `Subscription active — ${elig.subscription.exams_used} of ${elig.subscription.exam_allowance} exams used, ${elig.subscription.exams_remaining} remaining. An exam counts as soon as it starts.` : `${elig.free_attempts_remaining} free attempt(s) left. An attempt counts as soon as it starts.`}
            </p>
            <Button onClick={start} disabled={blocked || busy} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="start-exam-btn">
              {busy ? "Preparing…" : `Start · ${preview.duration_min} min`}
            </Button>
          </div>
          {!elig.can_start && elig.reason && <p className="text-sm text-red-600">{elig.reason} <Link to="/app/subscription" className="underline">View plans</Link></p>}
        </Card>
      )}
    </div>
  );
}

function Step({ n, label, children }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-3">{n}. {label}</p>
      {children}
    </div>
  );
}

function Chip({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-3.5 py-2 rounded-full text-sm border transition-colors ${active ? "bg-blue-50 border-blue-400 text-blue-700" : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"}`}>
      {children}
    </button>
  );
}
